import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for Web Speech API voice recognition
 *
 * Returns:
 * - transcript: Current transcribed text
 * - isListening: Whether actively recording
 * - isSupported: Whether browser supports Web Speech API
 * - error: Error message if any
 * - startListening: Function to start recording
 * - stopListening: Function to stop recording
 */
export function useVoiceRecognition() {
  const [transcript, setTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState(null);

  const recognitionRef = useRef(null);

  // Check browser support on mount
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition) {
      setIsSupported(true);

      // Initialize recognition instance
      const recognition = new SpeechRecognition();
      recognition.continuous = true; // Keep listening until manually stopped
      recognition.interimResults = true; // Show live transcription
      recognition.lang = 'en-US'; // Language setting

      recognitionRef.current = recognition;
    } else {
      setIsSupported(false);
      setError('Voice input not supported in this browser. Try Chrome, Edge, or Safari.');
    }

    // Cleanup on unmount
    return () => {
      if (recognitionRef.current && isListening) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
    };
  }, []); // Only run once on mount

  const startListening = useCallback(() => {
    if (!recognitionRef.current || isListening) return;

    const recognition = recognitionRef.current;

    // Clear previous state
    setTranscript('');
    setError(null);

    // Set up event handlers
    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
    };

    recognition.onresult = (event) => {
      // In continuous mode, accumulate all results from the entire session
      let interimTranscript = '';
      let finalParts = [];

      // Loop through ALL results (from 0, not just resultIndex)
      // to accumulate the full transcript from the session start
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          // Each final result is a natural speech segment (pause boundary)
          finalParts.push(result[0].transcript.trim());
        } else {
          interimTranscript += result[0].transcript;
        }
      }

      // Join final parts with [PAUSE] marker for punctuation API to use as sentence breaks
      // This preserves the natural speech pause boundaries
      const finalTranscript = finalParts.join(' [PAUSE] ');

      // Combine final + interim for full transcript
      const fullTranscript = (finalTranscript + (interimTranscript ? ' ' + interimTranscript : '')).trim();
      setTranscript(fullTranscript);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);

      // Map error types to user-friendly messages
      let errorMessage;
      switch (event.error) {
        case 'not-allowed':
        case 'service-not-allowed':
          errorMessage = 'Microphone access denied. Please enable in browser settings.';
          break;
        case 'no-speech':
          errorMessage = 'No speech detected. Try again?';
          break;
        case 'network':
          errorMessage = 'Network error. Check your connection.';
          break;
        case 'aborted':
          // User stopped it, not really an error
          errorMessage = null;
          break;
        default:
          errorMessage = `Speech recognition error: ${event.error}`;
      }

      if (errorMessage) {
        setError(errorMessage);
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    // Start recognition
    try {
      recognition.start();
    } catch (e) {
      console.error('Error starting recognition:', e);
      setError('Failed to start voice input. Please try again.');
      setIsListening(false);
    }
  }, [isListening]);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current || !isListening) return;

    try {
      recognitionRef.current.stop();
    } catch (e) {
      console.error('Error stopping recognition:', e);
    }
  }, [isListening]);

  return {
    transcript,
    isListening,
    isSupported,
    error,
    startListening,
    stopListening,
  };
}
